import { useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #3479 - everything technical (the held write's type, the refusal reason and
// the raw entry) sits inside the closed Technical details panel; the row's
// plain line (item.summary) says what happened in show office words.
// #3356 - the Detail cell of a quarantined-payload Review Queue row. The list
// is served off a GSI that drops the raw payload, so the held entry is fetched
// on demand (GET /quarantine/:id) when the admin opens it. Apply (admins only,
// `canApply`) lives INSIDE the opened panel so the person pressing it is
// looking at exactly what will be written. Apply replays the entry through
// score-api's normal validated write; the FE never writes a score itself. A
// refusal keeps the row and shows the server's new reason here.
function reasonText(reason) {
  if (!reason) return null;
  return reason.message || reason.code || null;
}

export default function QuarantinePayloadDetail({ item, canApply, onApplied }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const s = item.subject ?? {};
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(null); // the full quarantine row
  const [loadError, setLoadError] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [applyFailure, setApplyFailure] = useState(null); // { reason } | { unsupported } | { error }

  const failureText = s.failureMessage || s.failureCode;

  // #3551 - the original refusal in show office words. Static keys only (no
  // dynamic i18n keys), so the code to key map is spelled out here. An unknown
  // code falls back to the server's own message, then to the bare code.
  const whyHeld = (() => {
    switch (s.failureCode) {
      case "not_assigned":
        return t("reviewQueue.quarantine.why.not_assigned");
      case "forbidden":
        return t("reviewQueue.quarantine.why.forbidden");
      case "ride_not_found":
        return t("reviewQueue.quarantine.why.ride_not_found");
      case "class_not_found":
        return t("reviewQueue.quarantine.why.class_not_found");
      case "sheet_completed":
        return t("reviewQueue.quarantine.why.sheet_completed");
      case "marks_confirmed_frozen":
        return t("reviewQueue.quarantine.why.marks_confirmed_frozen");
      case "stale_write":
        return t("reviewQueue.quarantine.why.stale_write");
      case "duplicate_score_sheet":
        return t("reviewQueue.quarantine.why.duplicate_score_sheet");
      case "test_mismatch":
        return t("reviewQueue.quarantine.why.test_mismatch");
      case "validation_error":
        return t("reviewQueue.quarantine.why.validation_error");
      case "judge_not_found":
      case "judge_not_on_panel":
        return t("reviewQueue.quarantine.why.judge_not_on_panel");
      default:
        return failureText || null;
    }
  })();
  const usesAdminLogin = s.failureCode === "not_assigned" || s.failureCode === "forbidden";

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) {
      setLoadError(false);
      try {
        setLoaded(unwrap(await scoreApi.quarantine.getQuarantinedPayload(s.id)));
      } catch {
        setLoadError(true);
      }
    }
  };

  const apply = async () => {
    setConfirming(false);
    setApplying(true);
    setApplyFailure(null);
    try {
      unwrap(await scoreApi.quarantine.applyQuarantinedPayload(s.id, {}));
      await onApplied?.();
    } catch (err) {
      const body = err?.response?.data;
      if (body?.code === "quarantine_apply_failed") {
        setApplyFailure({ reason: body?.data?.reason ?? null });
      } else if (body?.code === "unsupported_op_type") {
        setApplyFailure({ unsupported: true });
      } else if (body?.code === "missing_base_version") {
        setApplyFailure({ missingBaseVersion: true });
      } else {
        setApplyFailure({ error: true });
      }
      // already_resolved: another admin cleared it; the refetch drops the row.
      if (body?.code === "already_resolved") await onApplied?.();
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={toggle} data-testid="quarantine-toggle-payload">
        {open ? t("reviewQueue.technicalDetails.hide") : t("reviewQueue.technicalDetails.show")}
      </Button>
      {open && (
        <Box sx={{ mt: 1 }} data-testid="quarantine-payload-panel">
      <Typography variant="body2" color="text.secondary" data-testid="quarantine-op-type">
        {t("reviewQueue.quarantine.opType", { opType: s.opType })}
      </Typography>
      <Typography variant="body2" color="text.secondary" data-testid="quarantine-failure-reason">
        {failureText
          ? t("reviewQueue.quarantine.failureReason", { reason: failureText })
          : t("reviewQueue.quarantine.noFailureReason")}
      </Typography>
          <Typography variant="subtitle2" color="text.primary">
            {t("reviewQueue.quarantine.payloadHeading")}
          </Typography>
          {loadError ? (
            <Alert severity="error">{t("reviewQueue.quarantine.payloadLoadError")}</Alert>
          ) : !loaded ? (
            <Typography variant="body2" color="text.secondary">
              {t("reviewQueue.quarantine.payloadLoading")}
            </Typography>
          ) : (
            <Box
              component="pre"
              data-testid="quarantine-payload"
              sx={{
                m: 0,
                p: 1,
                maxHeight: 280,
                overflow: "auto",
                fontSize: 12,
                color: "text.primary",
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              {JSON.stringify(loaded.payload, null, 2)}
            </Box>
          )}
          {canApply && s.applyable && loaded && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t("reviewQueue.quarantine.applyHint")}
              </Typography>
              <Button
                size="small"
                variant="contained"
                sx={{ mt: 0.5 }}
                disabled={applying || confirming}
                onClick={() => {
                  setApplyFailure(null);
                  setConfirming(true);
                }}
                data-testid="quarantine-apply"
              >
                {applying ? t("reviewQueue.quarantine.applying") : t("reviewQueue.quarantine.apply")}
              </Button>
              {confirming && (
                <Alert severity="warning" sx={{ mt: 1 }} data-testid="quarantine-apply-confirm-panel">
                  <Typography variant="body2" data-testid="quarantine-apply-reason">
                    {whyHeld
                      ? t("reviewQueue.quarantine.confirmWhy", { reason: whyHeld })
                      : t("reviewQueue.quarantine.confirmNoWhy")}
                  </Typography>
                  {usesAdminLogin && (
                    <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="quarantine-apply-admin-login">
                      {t("reviewQueue.quarantine.confirmAdminLogin")}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t("reviewQueue.quarantine.confirmQuestion")}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" variant="contained" onClick={apply} data-testid="quarantine-apply-confirm">
                      {t("reviewQueue.quarantine.confirmApply")}
                    </Button>
                    <Button
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() => setConfirming(false)}
                      data-testid="quarantine-apply-cancel"
                    >
                      {t("reviewQueue.quarantine.confirmCancel")}
                    </Button>
                  </Box>
                </Alert>
              )}
            </Box>
          )}
          {applyFailure && (
            <Alert severity="error" sx={{ mt: 1 }} data-testid="quarantine-apply-error">
              {applyFailure.reason
                ? t("reviewQueue.quarantine.applyFailed", { reason: reasonText(applyFailure.reason) ?? "" })
                : applyFailure.unsupported
                  ? t("reviewQueue.quarantine.applyUnsupported")
                  : applyFailure.missingBaseVersion
                    ? t("reviewQueue.quarantine.applyMissingBaseVersion")
                    : applyFailure.error
                      ? t("reviewQueue.quarantine.applyError")
                      : t("reviewQueue.quarantine.applyFailedNoReason")}
            </Alert>
          )}
        </Box>
      )}
    </>
  );
}
